import type { OwnerStatus, OwnerStatusBoat, OwnerStatusHeatmapCell } from '@sea-snag/shared';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { type ReactNode, useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { publicOwnerApi } from '../lib/api-client';
import { columnIndexFor, WEEK_COUNT } from '../lib/heatmap-columns';
import { queryKeys } from '../lib/query-keys';
import { mergeYearOptions } from '../lib/year-options';

export const Route = createFileRoute('/owner-status/$token')({
  component: OwnerStatusPage,
});

const MONTHS = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const chartConfig = {
  ownerEarned: { label: 'Earnings', color: 'var(--chart-1)' },
} satisfies ChartConfig;

function money(currency: string | null, value: number): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency ?? 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

function OwnerStatusPage() {
  const { token } = Route.useParams();
  // Page-local, deliberately: this link is opened by an owner on their own
  // device, outside the app shell and its header year control.
  const [year, setYear] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.ownerStatus.detail(token, year === null ? 'latest' : String(year)),
    queryFn: () => publicOwnerApi.status(token, year ?? undefined),
    retry: false,
    // Keep the previous year on screen while the next one loads, so switching
    // year does not blank the whole page.
    placeholderData: (previous) => previous,
  });

  if (isLoading) {
    return (
      <Centered>
        <Skeleton className="h-[420px] w-full max-w-4xl" />
      </Centered>
    );
  }
  if (isError || !data) {
    return (
      <Centered>
        <Card className="max-w-md">
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            This link is invalid or has been turned off. Please contact your charter manager.
          </CardContent>
        </Card>
      </Centered>
    );
  }

  const s: OwnerStatus = data;
  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div>
        <div className="text-sm text-muted-foreground">{s.organizationName} · Owner view</div>
        <h1 className="text-2xl font-bold">
          Hi {s.ownerName}, here&apos;s how your boats are doing
        </h1>
        <div className="mt-2">
          <Select value={String(s.year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-8 w-[92px]" aria-label="Year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {mergeYearOptions(new Date().getFullYear(), s.year, s.years).map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Kpi label="Earnings" value={money(s.currency, s.totals.ownerEarned)} />
        <Kpi label="Bookings" value={String(s.totals.bookings)} />
        <Kpi label="Weeks booked" value={String(s.totals.weeksBooked)} />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Monthly earnings</CardTitle>
        </CardHeader>
        <CardContent>
          <MonthlyChart status={s} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Your boats</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {s.boats.map((b) => (
            <BoatRow key={b.boatPid} boat={b} currency={s.currency} />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Booked weeks · {s.year}</CardTitle>
        </CardHeader>
        <CardContent>
          <Heatmap status={s} />
        </CardContent>
      </Card>
    </div>
  );
}

function MonthlyChart({ status }: { status: OwnerStatus }) {
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency: status.currency ?? 'EUR',
        maximumFractionDigits: 0,
      }),
    [status.currency]
  );
  const data = useMemo(
    () => status.monthly.map((m) => ({ label: MONTHS[m.month], ownerEarned: m.ownerEarned })),
    [status.monthly]
  );

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-[260px] w-full">
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="fill-ownerEarned" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--color-ownerEarned)" stopOpacity={0.8} />
            <stop offset="95%" stopColor="var(--color-ownerEarned)" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} fontSize={11} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => formatter.format(v)}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Area
          dataKey="ownerEarned"
          type="monotone"
          fill="url(#fill-ownerEarned)"
          stroke="var(--color-ownerEarned)"
        />
      </AreaChart>
    </ChartContainer>
  );
}

function BoatRow({ boat, currency }: { boat: OwnerStatusBoat; currency: string | null }) {
  return (
    <div className="flex items-center justify-between border-b py-2 last:border-0">
      <div>
        <div className="font-medium">{boat.name}</div>
        <div className="text-xs text-muted-foreground">
          {boat.bookings} bookings
          {boat.nextCharterFrom
            ? ` · next charter ${boat.nextCharterFrom}`
            : ' · no upcoming charters'}
          {boat.sharePct != null ? ` · ${boat.sharePct}% share` : ''}
        </div>
      </div>
      <div className="text-right tabular-nums">
        {boat.ownerEarned != null ? money(currency, boat.ownerEarned) : '—'}
      </div>
    </div>
  );
}

// Light emerald scale: more booked days → deeper green.
function bucketFor(bookedDays: number): string {
  if (bookedDays >= 7) return 'bg-emerald-500';
  if (bookedDays >= 5) return 'bg-emerald-400';
  if (bookedDays >= 3) return 'bg-emerald-300';
  if (bookedDays >= 1) return 'bg-emerald-200';
  return 'bg-muted/50';
}

function fmtDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function Heatmap({ status }: { status: OwnerStatus }) {
  const byBoat = new Map<string, Map<number, OwnerStatusHeatmapCell>>();
  for (const cell of status.heatmap) {
    const col = columnIndexFor(cell.weekStart, status.year);
    if (col === null) continue;
    const inner = byBoat.get(cell.boatPid) ?? new Map<number, OwnerStatusHeatmapCell>();
    inner.set(col, cell);
    byBoat.set(cell.boatPid, inner);
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div className="overflow-x-auto">
        <div
          className="grid items-center gap-x-[3px] gap-y-2 text-xs"
          style={{ gridTemplateColumns: `minmax(120px, 160px) repeat(${WEEK_COUNT}, 12px)` }}
        >
          <div />
          {Array.from({ length: WEEK_COUNT }).map((_, i) => (
            <div
              key={`hdr-${i}`}
              className="text-center text-[10px] text-muted-foreground"
              aria-hidden
            >
              {(i + 1) % 4 === 1 ? i + 1 : ''}
            </div>
          ))}
          {status.boats.map((boat) => {
            const inner = byBoat.get(boat.boatPid) ?? new Map<number, OwnerStatusHeatmapCell>();
            return (
              <div className="contents" key={boat.boatPid}>
                <div className="truncate pr-2 font-medium" title={boat.name}>
                  {boat.name}
                </div>
                {Array.from({ length: WEEK_COUNT }).map((_, i) => {
                  const cell = inner.get(i);
                  const bookedDays = cell?.bookedDays ?? 0;
                  const when = cell
                    ? `${fmtDay(cell.weekStart)} – ${fmtDay(cell.weekEnd)}`
                    : `Week ${i + 1}`;
                  const state =
                    bookedDays > 0
                      ? `${bookedDays} booked day${bookedDays === 1 ? '' : 's'}`
                      : 'Available';
                  return (
                    <Tooltip key={`${boat.boatPid}-${i}`}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={`${boat.name}, ${when}: ${state}`}
                          className={cn(
                            'h-4 w-3 rounded-[2px] border border-black/5 transition-colors hover:ring-1 hover:ring-foreground/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground',
                            bucketFor(bookedDays)
                          )}
                        />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        <div className="font-medium">{boat.name}</div>
                        <div className="text-muted-foreground">{when}</div>
                        <div>{state}</div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
        <span>Available</span>
        <span className="size-3 rounded-[2px] bg-muted/50" />
        <span className="size-3 rounded-[2px] bg-emerald-200" />
        <span className="size-3 rounded-[2px] bg-emerald-300" />
        <span className="size-3 rounded-[2px] bg-emerald-400" />
        <span className="size-3 rounded-[2px] bg-emerald-500" />
        <span>Fully booked</span>
      </div>
    </TooltipProvider>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center p-6">{children}</div>;
}
